// src/encounter/lib/care-plan-readout-text.ts
//
// The care-plan readout's words (care-plan-readout.tsx), kept apart so the component file exports only components
// (react-refresh/only-export-components; #944 review). Used by the readout, the patient's Care plan tab, the L360 activation panel
// and the assign-care-plan text. PURE: no React.
import type { CarePlanView, GoalView } from './care-plan-view';
import { goalsOnTarget } from './care-plan-honest';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** "18 Jul 2026" (D Mon YYYY) from an ISO string, by its UTC calendar date; the raw text when unreadable. */
export function formatReadoutDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return `${d.getUTCDate()} ${MONTHS[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
}

/** "Target <5.7 % · ADA 2026 · due 24 Dec 2026" (D-43 §4). */
export function targetLine(goal: GoalView): string {
  if (!goal.target) return 'Target not readable';
  const { comparator, value, unit, source } = goal.target;
  const parts = [`Target ${comparator}${value}${unit ? ` ${unit}` : ''}`, source ?? 'source not recorded'];
  if (goal.dueDate) parts.push(`due ${formatReadoutDate(goal.dueDate)}`);
  return parts.join(' · ');
}

/** "Latest 5.3 % as of 18 Jul 2026": the lab's text when it sent one, else the value (D-43 §4). */
export function latestLine(goal: GoalView): string {
  const l = goal.latest;
  if (!l) return 'No result on file';
  const shown = l.valueText ?? (l.value === null ? '' : String(l.value));
  return `Latest ${shown}${l.unit ? ` ${l.unit}` : ''} as of ${formatReadoutDate(l.date)}`;
}

/**
 * The collapsed row: the PATIENT'S enrolment, never this visit's purpose.
 *
 * It used to read "{programme} · {step}", drawn in the A&P of a visit booked for an unrelated reason
 * (dev test walk, 1 Oct): the step read as this visit's reason and contradicted the header's
 * "Reason for Visit". Nothing on the wire says this appointment IS the step's visit (C4's
 * `currentStep` is `{ id, name }` only, C5 carries no appointment or encounter), so the line cannot
 * claim it and does not: an active plan says the patient is enrolled, and its step is named as the
 * CARE PLAN's step (Samer, 18 Oct: "care plan", not "programme"). A plan that is not active keeps
 * its title alone, as before (it never carries a step: `withCurrentSteps` only joins one to an active plan).
 */
export function planLine(plan: CarePlanView): string {
  if (plan.status !== 'active') return plan.title;
  const enrolled = `Enrolled in ${plan.title}`;
  return plan.currentStep.state === 'known' ? `${enrolled} · care plan step: ${plan.currentStep.name}` : enrolled;
}

/** `honest` (`?x=prevent-honest`): a 'met' verdict counts only with a result behind it (care-plan-honest.ts). */
export function tally(plan: CarePlanView, honest = false): string {
  const met = honest ? goalsOnTarget(plan.goals) : plan.goals.filter((g) => g.status === 'met').length;
  return `Goals on target ${met}/${plan.goals.length}`;
}
